import React, { useState } from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import { useEditorStore, EditorField } from "../../../store";
import { DebouncedTextField } from "../../common/DebouncedTextField";
import { buildAdminAppUrl } from "../../../services/admin-deeplink";
import { ColorRow } from "./SettingRows";

export const FieldSettingsPanel: React.FC = () => {
  const selectedFieldId = useEditorStore((state) => state.ui.selectedFieldId);
  const fields = useEditorStore((state) => state.config.fields);
  const updateField = useEditorStore((state) => state.updateField);
  const updateComponentStyle = useEditorStore(
    (state) => state.updateComponentStyle
  );
  const componentSettings = useEditorStore(
    (state) => state.config.design.settings.components
  );

  // Find the selected field
  const selectedField = fields.find(
    (f: EditorField) => f.tempId === selectedFieldId
  );

  if (!selectedField) {
    return (
      <div className="p-4 text-center">
        <s-text color="subdued">Select a field to edit its properties.</s-text>
      </div>
    );
  }

  // `offer_embed` fields are configured in their own upsell editor — the
  // form builder doesn't expose any per-field controls for them. Show a
  // pointer to the right editor instead of the standard input panel.
  if (selectedField.type === "offer_embed") {
    return <OfferEmbedSettingsPanel field={selectedField} />;
  }

  const isLocked = selectedField.locked === true;

  const handleUpdate = (
    key: string,
    value: string | number | boolean | Record<string, unknown>
  ) => {
    updateField(selectedField.tempId, { [key]: value } as Partial<EditorField>);
  };

  // Clears a per-field style override so the field inherits the global value
  // again. Sending `undefined` removes it from serialization (the `!= null`
  // guard skips it), unlike "" which would persist as an empty override.
  const handleClear = (key: string) => {
    updateField(selectedField.tempId, {
      [key]: undefined,
    } as Partial<EditorField>);
  };

  const INPUT_TYPES = [
    "text",
    "email",
    "tel",
    "number",
    "textarea",
    "date",
    "select",
  ];
  const isInputType = INPUT_TYPES.includes(selectedField.type);

  // Helper for Ordered Summary
  const orderSummarySettings = componentSettings?.order_summary || {};

  const handleOrderSummaryUpdate = (key: string, value: unknown) => {
    updateComponentStyle("order_summary", key, value);
  };

  return (
    <div className="min-w-0">
      <s-stack gap="base" direction="block">
        <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
          <div className="bg-indigo-50 text-indigo-700 text-xs font-semibold px-2 py-1 rounded uppercase tracking-wider">
            {selectedField.type}
          </div>
          {isLocked && (
            <div
              className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1"
              title="Campo requerido por el sistema"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Bloqueado
            </div>
          )}
        </div>

        {/* --- GENERAL SETTINGS --- */}
        <div className="flex flex-col gap-5">
          <s-text type="strong" color="subdued">
            General
          </s-text>

          {/* Label */}
          <DebouncedTextField
            label="Etiqueta"
            name="label"
            value={selectedField.label}
            onInput={(e) => handleUpdate("label", e.currentTarget.value)}
          />

          {/* Texto interno — only for checkbox, shown here instead of the validation section */}
          {selectedField.type === "checkbox" && (
            <DebouncedTextField
              label="Texto interno"
              name="placeholder"
              value={selectedField.placeholder || ""}
              placeholder="Ej: Acepto los términos y condiciones"
              onInput={(e) =>
                handleUpdate("placeholder", e.currentTarget.value)
              }
            />
          )}

          {/* Internal Name / Key */}
          {/*
          <DebouncedTextField
            label="Nombre Interno (Solo Admin)"
            name="internal_text"
            value={selectedField.internal_text || ""}
            placeholder="e.g. Customer Name"
            onInput={(e) =>
              handleUpdate("internal_text", e.currentTarget.value)
            } */}

          {/* Key (ReadOnly) */}
          {/* <div className="bg-gray-50 p-2 rounded border border-gray-100 text-xs">
            <span className="font-semibold text-gray-500">System Key:</span>{" "}
            <code className="text-gray-700">{selectedField.key}</code>
          </div> */}

          {/* Required toggle — hidden for order_summary; read-only if locked */}
          {"required" in selectedField &&
            selectedField.type !== "order_summary" &&
            (isLocked ? (
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded p-2.5 text-sm">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-400 shrink-0"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span className="text-gray-500">
                  Required —{" "}
                  <span className="font-medium text-gray-600">
                    locked by system
                  </span>
                </span>
              </div>
            ) : (
              <s-select
                label="Requerido"
                name="required"
                value={selectedField.required ? "true" : "false"}
                onChange={(e) =>
                  handleUpdate("required", e.currentTarget.value === "true")
                }
              >
                <s-option value="false">Opcional</s-option>
                <s-option value="true">Requerido</s-option>
              </s-select>
            ))}
        </div>

        {/* --- INPUT SPECIFIC SETTINGS --- */}
        {isInputType && selectedField.type !== "checkbox" && (
          <div className="border-t border-gray-200 pt-3 mt-3 flex flex-col gap-5">
            <s-text type="strong" color="subdued">
              Visualización & validación
            </s-text>

            {/* Placeholder */}
            <DebouncedTextField
              label="Placeholder"
              name="placeholder"
              value={selectedField.placeholder || ""}
              onInput={(e) =>
                handleUpdate("placeholder", e.currentTarget.value)
              }
            />

            {/* Options editor — only for unlocked select fields */}
            {selectedField.type === "select" && !isLocked && (
              <OptionsEditor
                key={selectedField.tempId}
                field={selectedField}
                onCommit={(options) =>
                  updateField(selectedField.tempId, {
                    options,
                  } as Partial<EditorField>)
                }
              />
            )}

            {/* Show Icon */}
            <s-checkbox
              label="Mostrar Icono del Campo"
              checked={selectedField.show_icon || false}
              onChange={(e: CallbackEvent<"s-checkbox">) =>
                handleUpdate("show_icon", e.currentTarget.checked)
              }
            />

            {/* Error Message — pending implementation (not persisted yet) */}
            {/* <DebouncedTextField
                label="Mensaje de Error (Personalizado)"
                name="error_message"
                value={selectedField.error_message || ""}
                placeholder="Mensaje de error predeterminado"
                onInput={(e) =>
                  handleUpdate("error_message", e.currentTarget.value)
                }
              /> */}
          </div>
        )}

        {/* --- ORDER SUMMARY SPECIFIC --- */}
        {selectedField.type === "order_summary" && (
          <div className="border-t border-gray-200 pt-3 mt-3 flex flex-col gap-5">
            <s-text type="strong" color="subdued">
              Textos del Resumen
            </s-text>

            <DebouncedTextField
              label="Etiqueta Subtotal"
              name="subtotal_text"
              value={orderSummarySettings.subtotal_text ?? "Subtotal"}
              onInput={(e) =>
                handleOrderSummaryUpdate("subtotal_text", e.currentTarget.value)
              }
            />
            <DebouncedTextField
              label="Etiqueta Envío"
              name="shipping_text"
              value={orderSummarySettings.shipping_text ?? "Envío"}
              onInput={(e) =>
                handleOrderSummaryUpdate("shipping_text", e.currentTarget.value)
              }
            />
            <DebouncedTextField
              label="Etiqueta del Total"
              name="total_text"
              value={orderSummarySettings.total_text ?? "Total"}
              onInput={(e) =>
                handleOrderSummaryUpdate("total_text", e.currentTarget.value)
              }
            />

            <s-checkbox
              label="Mostrar Aviso de Impuestos"
              checked={orderSummarySettings.show_tax_notice || false}
              onChange={(e: CallbackEvent<"s-checkbox">) =>
                handleOrderSummaryUpdate(
                  "show_tax_notice",
                  e.currentTarget.checked
                )
              }
            />
          </div>
        )}

        {/* --- STYLING --- */}
        <div className="border-t border-gray-200 pt-3 mt-3 flex flex-col gap-5">
          <s-text type="strong" color="subdued">
            Estilos
          </s-text>

          {/* Width — pending implementation (not persisted yet) */}
          {/* <s-select
            label="Ancho del Campo"
            name="width"
            value={selectedField.style?.width || "100%"}
            onChange={(event) =>
              handleUpdate("style", {
                ...selectedField.style,
                width: event.currentTarget.value,
              })
            }
          >
            <s-option value="100%">Full Width (100%)</s-option>
            <s-option value="50%">Half Width (50%)</s-option>
          </s-select> */}

          {/* Per-field border color override — applies to all field types */}
          <ColorRow
            label="Color del Borde"
            name="field_border_color"
            value={selectedField.border_color}
            fallback="#e0e0e0"
            onChange={(value) => handleUpdate("border_color", value)}
            onClear={() => handleClear("border_color")}
          />

          {/* Per-field background color override — applies to all field types */}
          <ColorRow
            label="Color de Fondo"
            name="field_background_color"
            value={selectedField.field_background_color}
            fallback="#ffffff"
            onChange={(value) => handleUpdate("field_background_color", value)}
            onClear={() => handleClear("field_background_color")}
          />
        </div>
      </s-stack>
    </div>
  );
};

// ─── Options editor ──────────────────────────────────────────────────────────

type SelectOption = { label: string; value: string };

// Keyed by the field's tempId at the call site so it remounts (and re-seeds its
// draft from the store) when the user switches between select fields. Keeps a
// local draft while typing and commits to the store on blur.
const OptionsEditor: React.FC<{
  field: { options?: SelectOption[] };
  onCommit: (options: SelectOption[]) => void;
}> = ({ field, onCommit }) => {
  const [raw, setRaw] = useState(() =>
    (field.options ?? []).map((o) => o.label).join(",")
  );

  return (
    <div className="flex flex-col gap-2">
      <label className="block text-sm font-medium text-gray-700">
        Opciones del Dropdown
      </label>
      <textarea
        rows={4}
        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
        placeholder="Value 1,Value 2,Value 3"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() =>
          onCommit(
            raw
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v.length > 0)
              .map((v) => ({ label: v, value: v }))
          )
        }
      />
      <p className="text-xs text-gray-400">
        Ingresa los valores separados por coma. Ejemplo:{" "}
        <span className="text-indigo-500">Roma,Paris,New York,Budapest</span>
      </p>
    </div>
  );
};

// ─── Offer embed panel ───────────────────────────────────────────────────────

const OFFER_EMBED_LABELS: Record<
  "one_tick" | "quantity_tiers" | "bundle",
  { title: string; editorPath: string }
> = {
  one_tick: {
    title: "Upsell 1-tick",
    editorPath: "sales-booster",
  },
  quantity_tiers: {
    title: "Quantity tiers",
    editorPath: "sales-booster/quantity-offers",
  },
  bundle: {
    title: "Bundle",
    editorPath: "sales-booster/bundles",
  },
};

interface OfferEmbedField {
  type: "offer_embed";
  display_type: "one_tick" | "quantity_tiers" | "bundle";
  offer_id: string;
}

const OfferEmbedSettingsPanel: React.FC<{ field: OfferEmbedField }> = ({
  field,
}) => {
  const labels = OFFER_EMBED_LABELS[field.display_type];

  const handleOpenEditor = () => {
    const url = buildAdminAppUrl(labels.editorPath);
    window.open(url, "_blank");
  };

  return (
    <div className="min-w-0">
      <s-stack gap="large-400" direction="block">
        <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
          <div className="bg-indigo-50 text-indigo-700 text-xs font-semibold px-2 py-1 rounded uppercase tracking-wider">
            offer
          </div>
          <s-text type="strong">{labels.title}</s-text>
        </div>

        <s-banner tone="info">
          <s-stack direction="block" gap="small-200">
            <s-text>
              Este campo es un upsell. Su contenido y diseño se configuran desde
              el editor del Impulsor de Ventas.
            </s-text>
            <s-button variant="secondary" onClick={handleOpenEditor}>
              Ir al editor del upsell
            </s-button>
          </s-stack>
        </s-banner>
      </s-stack>
    </div>
  );
};
